/**
 * Auth-aware extension of the user avatar component
 * Automatically updates with user profile information when auth service is available
 */
import { QdsUserAvatar } from './qds-user-avatar.js';
import type { QuantumAuthService, Subscription } from '../../types';

/**
 * Menu item interface for avatar dropdown menu
 */
export interface MenuItemConfig {
  id: string;
  label: string;
  icon?: string; // SVG path or icon name
  action?: () => void;
  type?: 'default' | 'logout' | 'custom';
}

/**
 * @element qds-auth-avatar
 * 
 * A user avatar component that automatically displays user information from auth service.
 * Extends the base QdsUserAvatar with auth-awareness and a customizable user menu.
 * 
 * When connected and the auth service is available:
 * - Displays the user's profile picture from Microsoft Graph API when authenticated
 * - Shows a dropdown menu with user actions (Profile, Logout, etc.) when avatar is clicked
 * - With auto-login enabled, will show a login button when not authenticated
 * 
 * The dropdown menu is fully customizable - clients can add, remove, or modify menu items.
 * 
 * @attr {boolean} auto-login - Whether to show login UI when not authenticated
 * @attr {string} auth-config - JSON configuration for auth service initialization
 * 
 * @csspart login-button - The login button shown when not authenticated
 * @csspart avatar-dropdown - The dropdown menu container
 * @csspart menu-item - Individual menu items in the dropdown
 * 
 * @fires login - When the login button is clicked
 * @fires menu-item-selected - When a menu item is clicked (detail: {itemId, itemLabel})
 * @fires logout - When the logout menu item is selected
 */
export class QdsAuthAvatar extends QdsUserAvatar {
  // Simple class with minimal state
  
  private _isAuthenticated: boolean = false;
  private _authService: QuantumAuthService | null = null;
  private _authSubscription: Subscription | null = null;
  private _autoLogin: boolean = false;
  private _authConfig: string | null = null;
  
  // Dropdown menu state
  private _dropdownOpen: boolean = false;
  private _menuItems: Map<string, MenuItemConfig> = new Map();
  private _logoutMenuItem: MenuItemConfig;
  private _boundClickOutsideHandler?: (event: MouseEvent) => void;
  private _boundKeydownHandler?: (event: KeyboardEvent) => void;
  private _boundLoginClickHandler: (event: Event) => void;
  private _boundAuthInitializedHandler: () => void;
  private _boundAuthUserChangedHandler: () => void;
  private _boundAvatarClickHandler: (event: Event) => void;
  private _boundMenuItemClickHandler: (event: Event) => void;
  
  static get observedAttributes(): string[] {
    return [...super.observedAttributes, 'auto-login', 'auth-config'];
  }
  
  constructor() {
    super();
    this._boundLoginClickHandler = this._handleLoginClick.bind(this);
    this._boundAuthInitializedHandler = this._handleAuthInitialized.bind(this);
    this._boundAuthUserChangedHandler = this._handleUserChanged.bind(this);
    this._boundAvatarClickHandler = this._handleAvatarClick.bind(this);
    this._boundMenuItemClickHandler = this._handleMenuItemClick.bind(this);
    this._initializeDefaultMenuItems();
  }

  /**
   * Initialize default menu items for authenticated users
   */
  private _initializeDefaultMenuItems(): void {
    // Add default Logout menu item
    this._logoutMenuItem = {
      id: 'logout',
      label: 'Logout',
      type: 'logout',
      icon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19,3H5C3.9,3 3,3.9 3,5V9H5V5H19V19H5V15H3V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M11,7V10H1V14H11V17L16,12L11,7Z" fill="currentColor"/></svg>'
    };
  }

  /**
   * Add or update a menu item
   * @param id - Unique identifier for the menu item
   * @param config - Menu item configuration
   */
  public addMenuItem(id: string, config: MenuItemConfig): void {
    this._menuItems.set(id, { ...config, id });
    
    // Re-render if dropdown is open and authenticated
    if (this._dropdownOpen && this._isAuthenticated && this.shadowRoot) {
      this._renderDropdownMenu();
    }
  }

  /**
   * Remove a menu item by ID
   * @param id - Unique identifier of the menu item to remove
   */
  public removeMenuItem(id: string): void {
    this._menuItems.delete(id);
    
    // Re-render if dropdown is open and authenticated
    if (this._dropdownOpen && this._isAuthenticated && this.shadowRoot) {
      this._renderDropdownMenu();
    }
  }

  /**
   * Get all menu items
   */
  public getMenuItems(): MenuItemConfig[] {
    return Array.from(this._menuItems.values());
  }

  /**
   * Clear all menu items
   */
  public clearMenuItems(): void {
    this._menuItems.clear();
  }
  
  connectedCallback(): void {
    super.connectedCallback();
    
    // Try to find auth service in global scope
    this._checkForAuthService();
    
    // If auth service is available, subscribe to events
    if (this._authService) {
      this._subscribeToAuthEvents();
      this._updateFromUserInfo();
      
      // If auto-login is enabled and user is not authenticated, show login button
      if (this._autoLogin && !this._isAuthenticated) {
        this._renderLoginButton();
      }
    }
    
    // Add login button click handler if needed
    const loginButton = this.shadowRoot?.querySelector('.login-button');
    if (loginButton) {
      loginButton.addEventListener('click', this._boundLoginClickHandler);
    }

    // Setup dropdown handlers for authenticated state
    this._setupDropdownHandlers();
    
    // Also listen for auth initialization and user changes
    document.addEventListener('quantum-auth-initialized', this._boundAuthInitializedHandler);
    document.addEventListener('quantum-auth-user-changed', this._boundAuthUserChangedHandler);
  }

  /**
   * Setup event handlers for dropdown menu interactions
   */
  private _setupDropdownHandlers(): void {
    // Bind handlers for event delegation
    this._boundClickOutsideHandler = this._handleClickOutside.bind(this);
    this._boundKeydownHandler = this._handleKeydown.bind(this);

    // Note: We'll attach these listeners only when dropdown is opened
    // in the _toggleDropdown method
  }
  
  /**
   * Handle auth service initialization event
   */
  private _handleAuthInitialized(): void {
    // Check for auth service again and update if available
    this._checkForAuthService();
  }
  
  /**
   * Handle user authentication changes
   */
  private _handleUserChanged(): void {
    // Update avatar with the latest user information
    if (this._authService) {
      this._updateFromUserInfo();
      
      // If dropdown is open, refresh it with new user info
      if (this._dropdownOpen) {
        this._renderDropdownMenu();
      }
    } else {
      // Try to find auth service
      this._checkForAuthService();
    }
  }
  
  disconnectedCallback(): void {
    super.disconnectedCallback();
    
    // Close dropdown if open
    if (this._dropdownOpen) {
      this._closeDropdown();
    }
    
    // Unsubscribe from auth events
    if (this._authSubscription) {
      this._authSubscription.unsubscribe();
      this._authSubscription = null;
    }
    
    // Remove login button click handler
    const loginButton = this.shadowRoot?.querySelector('.login-button');
    if (loginButton) {
      loginButton.removeEventListener('click', this._boundLoginClickHandler);
    }
    
    // Remove dropdown event listeners
    this._removeDropdownListeners();
    
    // Remove auth initialization event listener
    document.removeEventListener('quantum-auth-initialized', this._boundAuthInitializedHandler);
    
    // Remove user changed event listener
    document.removeEventListener('quantum-auth-user-changed', this._boundAuthUserChangedHandler);
  }

  /**
   * Remove dropdown event listeners
   */
  private _removeDropdownListeners(): void {
    if (this._boundClickOutsideHandler) {
      document.removeEventListener('click', this._boundClickOutsideHandler);
    }
    if (this._boundKeydownHandler) {
      document.removeEventListener('keydown', this._boundKeydownHandler);
    }
  }
  
  // Helper method to trigger render in the parent class by updating an observed attribute
  private _triggerRender(): void {
    // Force an attribute update to trigger parent render
    // We do this by toggling a non-visible attribute temporarily
    const currentName = this.getAttribute('name') || '';
    this.setAttribute('name', currentName + ' ');
    this.setAttribute('name', currentName);
  }
  
  get autoLogin(): boolean {
    return this._autoLogin;
  }
  
  set autoLogin(value: boolean) {
    if (value) {
      this.setAttribute('auto-login', '');
    } else {
      this.removeAttribute('auto-login');
    }
  }
  
  /**
   * Check for auth service in global scope
   */
  private _checkForAuthService(): void {
    try {
      // Check for global auth service instance
      if (window.quantumAuth) {
        this._authService = window.quantumAuth;
        this._subscribeToAuthEvents();
        this._updateFromUserInfo();
        
        // If auto-login is enabled and user is not authenticated, show login button
        if (this._autoLogin && !this._isAuthenticated) {
          this._renderLoginButton();
        }
      } else {
        // Try to find it after a short delay (to allow for async loading)
        setTimeout(() => {
          if (window.quantumAuth) {
            this._authService = window.quantumAuth;
            this._subscribeToAuthEvents();
            this._updateFromUserInfo();
            
            // If auto-login is enabled and user is not authenticated, show login button
            if (this._autoLogin && !this._isAuthenticated) {
              this._renderLoginButton();
            }
          }
        }, 100);
      }
    } catch (error) {
      console.warn('Auth service not available for avatar component', error);
    }
  }
  
  /**
   * Subscribe to auth events
   */
  private _subscribeToAuthEvents(): void {
    if (!this._authService) return;

    if (this._authSubscription) {
      this._authSubscription.unsubscribe();
    }
    
    this._authSubscription = this._authService.subscribeToEvents(event => {
      if (event.type === 'login' || event.type === 'logout') {
        this._updateFromUserInfo();
      }
    });
  }
  
  /**
   * Update avatar from user information
   */
  private _updateFromUserInfo(): void {
    if (!this._authService) {
      return;
    }
    
    const userInfo = this._authService.getUserInfo();
    this._isAuthenticated = !!userInfo;
    
    if (userInfo) {
      // Set the display name which will be used for initials
      this.setAttribute('name', userInfo.displayName || '');
      
      // Default to online when authenticated
      if (!this.getAttribute('status')) {
        this.setAttribute('status', 'online');
      }
      
      // Try to get photo from user manager if available
      const userManager = this._authService.getUserManager();
      
      if (userManager && userManager.userPhoto) {
        // Set the image in all ways to ensure it's picked up
        const photoUrl = userManager.userPhoto;
        this._image = photoUrl;
        this.setAttribute('image', photoUrl);
        this.image = photoUrl; // Use setter
        
        // Force a render to ensure image is displayed
        this.render();
      } 
      // If no photo is already loaded, try to load it
      else if (!this._image && userManager && typeof userManager.loadUserPhoto === 'function') {
        userManager.loadUserPhoto().then(photoUrl => {
          if (photoUrl) {
            this._image = photoUrl;
            this.setAttribute('image', photoUrl);
            this.image = photoUrl; // Use setter
            this.render();
          }
        }).catch(err => {
          console.error('Error loading user photo:', err);
        });
      }
    } else if (this._autoLogin) {
      // If not authenticated and auto-login is enabled, render login button
      this._renderLoginButton();
    } else {
      // Not authenticated and no auto-login - just show initials with no info
      this.setAttribute('name', '');
      this.removeAttribute('image');
      this._image = '';
      this._triggerRender();
    }
  }
  
  /**
   * Handle login button click
   */
  private _handleLoginClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Change the button to indicate login in progress
    if (event.target instanceof HTMLElement) {
      event.target.textContent = 'Signing in...';
      if (event.target instanceof HTMLButtonElement) {
        event.target.disabled = true;
      }
    }
    
    // Store the current page URL for post-login redirect
    try {
      sessionStorage.setItem('auth_redirect_url', window.location.href);
      console.log('Stored redirect URL:', window.location.href);
    } catch (e) {
      console.warn('Failed to store redirect URL:', e);
    }
    
    // Dispatch login event
    this.dispatchEvent(new CustomEvent('login', {
      bubbles: true,
      composed: true
    }));
    
    // Try to login if auth service is available
    if (this._authService) {
      try {
        // Check if login method is available
        const authServiceAny = this._authService as any;
        if (typeof authServiceAny.login === 'function') {
          // Try popup first (better UX), auth service will fall back to redirect if popup fails
          authServiceAny.login(true).catch((error: Error) => {
            console.error('Login failed:', error);
            // Restore the button state
            this._renderLoginButton();
          });
        }
      } catch (error) {
        console.error('Error during login:', error);
        // Restore the button state
        this._renderLoginButton();
      }
    }
  }
  
  /**
   * Override attributeChangedCallback to handle auto-login and auth-config attributes
   * and trigger rendering in the base class
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    // Special handling for auto-login attribute
    if (name === 'auto-login') {
      this._autoLogin = newValue !== null;
      
      // If not authenticated and auto-login is enabled, show login button
      if (!this._isAuthenticated && this._autoLogin) {
        this._renderLoginButton();
        return;
      }
      
      // Otherwise trigger normal render for UI update
      this._triggerRender();
      return;
    }
    
    // Handle auth-config attribute
    if (name === 'auth-config') {
      this._authConfig = newValue;
      
      // If we have a config, try to initialize auth
      if (this._authConfig) {
        this._initializeAuthFromConfig();
      }
      
      return;
    }
    
    // For other attribute changes, let the base class handle it
    super.attributeChangedCallback(name, oldValue, newValue);
  }
  
  /**
   * Initialize auth service from config attribute
   */
  private async _initializeAuthFromConfig(): Promise<void> {
    if (!this._authConfig) return;

    try {
      // Check if auth service is already initialized globally
      // This prevents duplicate initialization when multiple avatars exist (e.g., desktop + mobile)
      if (window.quantumAuth) {
        console.log('Auth service already initialized, using existing instance');
        this._authService = window.quantumAuth;
        this._checkForAuthService();
        return;
      }

      // Parse the auth config
      const config = JSON.parse(this._authConfig);

      console.log('Initializing auth with config:', config);

      // Add auth redirect script if not already present
      this._ensureRedirectScriptLoaded();

      // Check if auth service is already loaded
      if (typeof window.initializeQuantumAuth !== 'function') {
        console.log('Loading auth service module...');

        // Dynamically load the auth service module
        const authModulePath = '/dev/assets/services/auth/index.js';
        await import(authModulePath);

        // Wait a bit for the module to register itself
        await new Promise(resolve => setTimeout(resolve, 100));

        if (typeof window.initializeQuantumAuth !== 'function') {
          throw new Error('Auth service module loaded but initializeQuantumAuth not available');
        }

        console.log('Auth service module loaded successfully');
      }

      // Double-check that auth wasn't initialized by another component while we were loading
      if (window.quantumAuth) {
        console.log('Auth service was initialized by another component, using that instance');
        this._authService = window.quantumAuth;
        this._checkForAuthService();
        return;
      }

      // Initialize auth service with the provided config
      console.log('Calling initializeQuantumAuth...');
      const authService = window.initializeQuantumAuth(config);
      window.quantumAuth = authService;

      // Initialize the auth service
      if (authService && typeof authService.initialize === 'function') {
        console.log('Initializing auth service...');
        await authService.initialize();
        console.log('Auth service initialized');
      }

      // Trigger an event to notify components
      document.dispatchEvent(new CustomEvent('quantum-auth-initialized'));

      // Let this component know auth is available
      this._checkForAuthService();
    } catch (error) {
      console.error('Error initializing auth from config:', error);

      // Show error in UI
      if (this.shadowRoot) {
        const loginButton = this.shadowRoot.querySelector('.login-button');
        if (loginButton) {
          loginButton.textContent = 'Auth Error';
        }
      }
    }
  }
  
  /**
   * Ensure the auth redirect script is loaded
   */
  private _ensureRedirectScriptLoaded(): void {
    // Check if script is already loaded
    if (document.getElementById('auth-redirect-script')) {
      return;
    }
    
    // Load the auth redirect script
    const script = document.createElement('script');
    script.id = 'auth-redirect-script';
    script.src = '/auth-redirect.js';
    script.async = true;
    
    // Add to head
    document.head.appendChild(script);
    console.log('Auth redirect script loaded');
  }
  
  /**
   * Render a login button instead of the avatar
   * We're calling the base class's render method after setting properties
   * to leverage the base implementation.
   */
  private _renderLoginButton(): void {
    if (!this.shadowRoot) return;
    
    // First, set the base avatar to have no image, no name
    // This will give us a blank initial state
    this.setAttribute('name', '');
    this.removeAttribute('image');
    
    // Trigger the parent's render method to get the basic structure
    this._triggerRender();
    
    // Now find the avatar element in the shadow DOM
    const avatarElement = this.shadowRoot.querySelector('.avatar');
    if (!avatarElement) return;
    
    // Clear the current content of the avatar and replace with login button
    avatarElement.innerHTML = `
      <button class="login-button" part="login-button" aria-label="Sign in">
        <svg class="login-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
        </svg>
        <span class="login-text">Sign in</span>
      </button>
    `;
    
    // Add styles for the login button
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .login-button {
        --_avatar-login-background: var(--avatar-login-background, var(--avatar-bg-color, var(--theme-accent-primary)));
        --_avatar-login-background-hover: var(--avatar-login-background-hover, var(--theme-accent-secondary));
        --_avatar-login-text: var(--avatar-login-text-color, var(--avatar-text-color, var(--theme-text-on-accent-primary)));
        width: 100%;
        height: 100%;
        border: none;
        background-color: var(--_avatar-login-background);
        color: var(--_avatar-login-text);
        cursor: pointer;
        font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
        font-weight: var(--theme-font-weight-medium, 500);
        font-size: var(--theme-font-size-xs, 12px);
        padding: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        transition:
          background-color var(--transition-speed-fast, 0.15s) ease,
          transform var(--transition-speed-fast, 0.15s) ease;
      }
      
      .login-icon {
        width: 16px;
        height: 16px;
        margin-bottom: 2px;
        fill: currentColor;
      }
      
      .login-text {
        font-size: 9px;
        letter-spacing: 0.5px;
      }
      
      .login-button:hover {
        background-color: var(--_avatar-login-background-hover);
      }
      
      .login-button:active {
        transform: scale(0.95);
      }

      .login-button:focus-visible {
        outline: 2px solid var(--theme-accent-primary);
        outline-offset: 2px;
      }
      
      .login-button:disabled {
        opacity: 0.7;
        cursor: wait;
      }
    `;
    
    // Append style to shadow DOM if it doesn't already exist
    if (!this.shadowRoot.querySelector('style.login-styles')) {
      styleElement.className = 'login-styles';
      this.shadowRoot.appendChild(styleElement);
    }
    
    // Add event listener to the login button
    const loginButton = this.shadowRoot.querySelector('.login-button');
    if (loginButton) {
      loginButton.addEventListener('click', this._boundLoginClickHandler);
    }
  }
  
  /**
   * Override the render method to ensure consistent image display
   */
  render(): void {
    // Use the parent render method
    super.render();
    
    // After parent render, wrap the avatar-container with the dropdown wrapper
    if (this._isAuthenticated && this.shadowRoot) {
      this._setupDropdownWrapper();
      this._attachDropdownToAvatar();
    }
    
  }

  /**
   * Setup dropdown wrapper structure in shadow DOM
   */
  private _setupDropdownWrapper(): void {
    if (!this.shadowRoot) return;

    const avatarContainer = this.shadowRoot.querySelector('.avatar-container');
    if (!avatarContainer) return;

    // Check if wrapper already exists
    if (avatarContainer.parentElement?.classList.contains('avatar-dropdown-wrapper')) {
      return;
    }

    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'avatar-dropdown-wrapper';

    // Move avatar-container into wrapper
    avatarContainer.parentNode?.insertBefore(wrapper, avatarContainer);
    wrapper.appendChild(avatarContainer);

    // Create dropdown container
    const dropdown = document.createElement('div');
    dropdown.className = 'avatar-dropdown';
    dropdown.setAttribute('part', 'avatar-dropdown');
    wrapper.appendChild(dropdown);

    // Ensure dropdown styles are present
    this._ensureDropdownStyles();
  }

  /**
   * Attach dropdown menu to avatar element
   */
  private _attachDropdownToAvatar(): void {
    if (!this.shadowRoot) return;

    const avatar = this.shadowRoot.querySelector('.avatar');
    if (!avatar) return;

    // Store the original click handler from parent if needed
    // We'll add our dropdown handler
    avatar.setAttribute('aria-haspopup', 'menu');
    avatar.setAttribute('aria-expanded', this._dropdownOpen ? 'true' : 'false');
    avatar.addEventListener('click', this._boundAvatarClickHandler);
  }

  /**
   * Ensure dropdown styles are present in shadow DOM
   */
  private _ensureDropdownStyles(): void {
    if (!this.shadowRoot) return;

    // Check if styles already exist
    if (this.shadowRoot.querySelector('style.dropdown-styles')) {
      return;
    }

    const styleElement = document.createElement('style');
    styleElement.className = 'dropdown-styles';
    styleElement.textContent = `
      .avatar-dropdown-wrapper {
        position: relative;
        display: inline-block;
      }

      .avatar-dropdown {
        --_avatar-dropdown-surface: var(--avatar-dropdown-background, var(--theme-background-elevated, var(--theme-background-primary)));
        --_avatar-dropdown-border: var(--avatar-dropdown-border-color, var(--theme-border-primary));
        --_avatar-dropdown-shadow: var(--avatar-dropdown-shadow, var(--theme-shadow-sm));
        --_avatar-dropdown-text: var(--avatar-dropdown-text-color, var(--theme-text-primary));
        --_avatar-dropdown-subtle-text: var(--avatar-dropdown-subtle-text-color, var(--theme-text-secondary));
        --_avatar-dropdown-hover: var(--avatar-dropdown-hover-background, var(--theme-background-hover));
        --_avatar-dropdown-active: var(--avatar-dropdown-active-background, var(--theme-background-tertiary));
        --_avatar-dropdown-danger-hover: var(--avatar-dropdown-danger-hover-background, var(--theme-background-error-subtle));
        position: absolute;
        top: 100%;
        right: 0;
        background-color: var(--_avatar-dropdown-surface);
        border: 1px solid var(--_avatar-dropdown-border);
        border-radius: var(--theme-radius-md, 8px);
        box-shadow: var(--_avatar-dropdown-shadow);
        min-width: 200px;
        z-index: 1000;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-8px);
        transition:
          opacity var(--transition-speed-normal, 0.25s) ease,
          visibility var(--transition-speed-normal, 0.25s) ease,
          transform var(--transition-speed-normal, 0.25s) ease;
        margin-top: 8px;
        color: var(--_avatar-dropdown-text);
        font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
      }

      .avatar-dropdown.open {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      .avatar-dropdown-user-info {
        padding: var(--theme-space-3, 12px) var(--theme-space-4, 16px);
        border-bottom: 1px solid var(--_avatar-dropdown-border);
      }

      .avatar-dropdown-user-name {
        font-weight: var(--theme-font-weight-semibold, 600);
        font-size: var(--theme-font-size-sm, 14px);
        color: var(--_avatar-dropdown-text);
        margin: 0;
      }

      .avatar-dropdown-user-email {
        font-size: var(--theme-font-size-xs, 12px);
        color: var(--_avatar-dropdown-subtle-text);
        margin: 4px 0 0 0;
      }

      .avatar-dropdown-items {
        padding: 8px 0;
      }

      .avatar-dropdown-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;
        color: var(--_avatar-dropdown-text);
        text-decoration: none;
        font-size: var(--theme-font-size-sm, 14px);
        cursor: pointer;
        transition: background-color var(--transition-speed-fast, 0.15s) ease;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
      }

      .avatar-dropdown-item:hover {
        background-color: var(--_avatar-dropdown-hover);
      }

      .avatar-dropdown-item:active {
        background-color: var(--_avatar-dropdown-active);
      }

      .avatar-dropdown-item:focus-visible {
        outline: 2px solid var(--theme-accent-primary);
        outline-offset: -2px;
      }

      .avatar-dropdown-item-icon {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--_avatar-dropdown-subtle-text);
        flex-shrink: 0;
      }

      .avatar-dropdown-item-icon svg {
        width: 100%;
        height: 100%;
      }

      .avatar-dropdown-item-icon .pi {
        font-size: var(--icon-size, 20px);
        line-height: 1;
        color: currentColor;
      }

      .avatar-dropdown-item-label {
        flex: 1;
      }

      .avatar-dropdown-item.logout:hover {
        background-color: var(--_avatar-dropdown-danger-hover);
      }

      .avatar-dropdown-item.logout .avatar-dropdown-item-icon {
        color: var(--theme-error);
      }

      .avatar-dropdown-separator {
        height: 1px;
        background-color: var(--_avatar-dropdown-border);
        margin: 8px 0;
      }

      .avatar-dropdown-item.logout {
        color: var(--theme-error);
      }
    `;

    this.shadowRoot.appendChild(styleElement);
  }

  /**
   * Handle avatar click to toggle dropdown
   */
  private _handleAvatarClick(event: Event): void {
    event.stopPropagation();
    
    if (this._isAuthenticated) {
      this._toggleDropdown();
    }
  }

  /**
   * Toggle dropdown visibility
   */
  private _toggleDropdown(): void {
    if (this._dropdownOpen) {
      this._closeDropdown();
    } else {
      this._openDropdown();
    }
  }

  /**
   * Open dropdown menu
   */
  private _openDropdown(): void {
    if (!this.shadowRoot) return;

    this._dropdownOpen = true;
    this._renderDropdownMenu();
    const avatar = this.shadowRoot.querySelector('.avatar');
    if (avatar) {
      avatar.setAttribute('aria-expanded', 'true');
    }

    // Add event listeners for closing
    if (this._boundClickOutsideHandler) {
      setTimeout(() => {
        document.addEventListener('click', this._boundClickOutsideHandler!);
      }, 0);
    }

    if (this._boundKeydownHandler) {
      document.addEventListener('keydown', this._boundKeydownHandler);
    }
  }

  /**
   * Close dropdown menu
   */
  private _closeDropdown(): void {
    if (!this.shadowRoot) return;

    this._dropdownOpen = false;

    const dropdown = this.shadowRoot.querySelector('.avatar-dropdown');
    if (dropdown) {
      dropdown.classList.remove('open');
    }

    const avatar = this.shadowRoot.querySelector('.avatar');
    if (avatar) {
      avatar.setAttribute('aria-expanded', 'false');
    }

    this._removeDropdownListeners();
  }

  /**
   * Render dropdown menu content
   */
  private _renderDropdownMenu(): void {
    if (!this.shadowRoot) return;

    const dropdown = this.shadowRoot.querySelector('.avatar-dropdown');
    if (!dropdown) return;

    const userInfo = this._authService?.getUserInfo();
    const displayName = userInfo?.displayName || 'User';
    const email = userInfo?.email || '';

    // Build user info section
    let html = `
      <div class="avatar-dropdown-user-info">
        <p class="avatar-dropdown-user-name">${this._escapeHtml(displayName)}</p>
        ${email ? `<p class="avatar-dropdown-user-email">${this._escapeHtml(email)}</p>` : ''}
      </div>
      <div class="avatar-dropdown-items">
    `;
    
    // Render non-logout items first
    this._menuItems.forEach((item) => {
      // Render separator if label is '-'
      if (item.label === '-') {
        html += this._renderSeparator();
        return;
      }

      const iconHtml = item.icon ? `<div class="avatar-dropdown-item-icon">${item.icon}</div>` : '';
      const classes = 'avatar-dropdown-item';

      html += `
        <button class="${classes}" data-item-id="${item.id}" part="menu-item">
          ${iconHtml}
          <div class="avatar-dropdown-item-label">${this._escapeHtml(item.label)}</div>
        </button>
      `;
    });

    if (this._menuItems.size > 0) {
      html += this._renderSeparator();
    }

    // Render logout item last
    const iconHtml = this._logoutMenuItem?.icon ? `<div class="avatar-dropdown-item-icon">${this._logoutMenuItem.icon}</div>` : '';
    html += `
      <button class="avatar-dropdown-item logout" data-item-id="${this._logoutMenuItem?.id}" part="menu-item">
        ${iconHtml}
        <div class="avatar-dropdown-item-label">${this._escapeHtml(this._logoutMenuItem?.label)}</div>
      </button>
    `;


    html += '</div>';

    dropdown.innerHTML = html;
    dropdown.classList.add('open');

    dropdown.removeEventListener('click', this._boundMenuItemClickHandler);
    dropdown.addEventListener('click', this._boundMenuItemClickHandler);
  }

  private _renderSeparator(): string {
    return `<div class="avatar-dropdown-separator"></div>`;
  }

  /**
   * Handle menu item click
   */
  private _handleMenuItemClick(event: Event): void {
    const target = event.target as HTMLElement;
    const button = target.closest('.avatar-dropdown-item') as HTMLElement;
    
    if (!button) return;

    const itemId = button.getAttribute('data-item-id');
    if (!itemId) return;

    const item = itemId === this._logoutMenuItem.id
      ? this._logoutMenuItem
      : this._menuItems.get(itemId);
    if (!item) return;

    // Close dropdown
    this._closeDropdown();

    // Handle default actions
    if (item.type === 'logout' || itemId === this._logoutMenuItem.id) {
      this._handleLogout();
    }

    // Call custom action if provided
    if (item.action && typeof item.action === 'function') {
      try {
        item.action();
      } catch (error) {
        console.error('Error executing menu item action:', error);
      }
    }

    // Dispatch generic menu item selected event
    this.dispatchEvent(new CustomEvent('menu-item-selected', {
      bubbles: true,
      composed: true,
      detail: { itemId, itemLabel: item.label, itemType: item.type }
    }));
  }

  /**
   * Handle logout action
   */
  private async _handleLogout(): Promise<void> {
    try {
      // Call auth service logout if available
      if (this._authService && typeof this._authService.logout === 'function') {
        await this._authService.logout();
      }

      // Dispatch logout event
      this.dispatchEvent(new CustomEvent('logout', {
        bubbles: true,
        composed: true
      }));
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  /**
   * Handle click outside dropdown
   */
  private _handleClickOutside(event: MouseEvent): void {
    if (!this.shadowRoot) return;

    const dropdown = this.shadowRoot.querySelector('.avatar-dropdown-wrapper');
    const target = event.target as Node;

    // Check if click is outside the dropdown wrapper
    if (dropdown && !dropdown.contains(target)) {
      this._closeDropdown();
    }
  }

  /**
   * Handle keydown for dropdown (e.g., Escape to close)
   */
  private _handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this._dropdownOpen) {
      this._closeDropdown();
      
      // Return focus to avatar
      const avatar = this.shadowRoot?.querySelector('.avatar');
      if (avatar instanceof HTMLElement) {
        avatar.focus();
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  private _escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-auth-avatar')) {
  customElements.define('qds-auth-avatar', QdsAuthAvatar);
}
